'use client';

import { useContext, useEffect, useState } from 'react';

import {
  useApolloClient,
  useReactiveVar,
  useSuspenseQuery,
} from '@apollo/client';
import { IconCheckmark } from '@veneer/core';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import { FlowOrchestratorContext } from '@storefront/app/flow-orchestrator/context';
import ThemeProviderWrapperV2 from '@storefront/app/ThemeProviderWrapperV2';
import {
  BundleItemOptionWithLowestPrice,
  BundleProductWithLowestPrice,
} from '@storefront/app/v2/pdp-config/printer/product-configuration/product-configuration.utils';
import { configurationState } from '@storefront/app/v2/pdp-config/printer/state-management/pdp-config-state';
import {
  classifyPrinter,
  getSkuList,
} from '@storefront/app/v2/pdp-config/printer/utils/utils';
import {
  GetPdpStateDocument,
  GetPdpStateQuery,
  GetSkuProductsDocument,
  GetSkuProductsQuery,
} from '@storefront/data-commerce';
import useCheckViewSize from '@storefront/hooks/useCheckViewSize';
import useVisibilityObserver from '@storefront/hooks/useVisibilityObserver';
import { NamespacesI18n } from '@storefront/i18n';
import AttentiveAnalyticsHandler from '@storefront/lib/AttentiveAnalytics';
import { ProductOptions } from '@storefront/types/Product';
import { getCurrencySymbol } from '@storefront/utils/bundlePriceCalculator';
import {
  getMonthlyPages,
  getProductTypeLabelFromOption,
} from '@storefront/utils/prodRequests';
import { formatPrice } from '@storefront/utils/utils';

import styles from './order-details.module.scss';
import {
  getActiveItemInfo,
  getBundleLevelImage,
  getImageClass,
  getOptionalItems,
  getRequiredItems,
  optionalOptionsSelected,
  requiredOptionsSelected,
  shouldShowDescriptions,
} from './order-details.utils';
import { ContinueButton } from '../continue-button/continue-button';
import OrderDetailsScrollBar from '../order-details-scroll-bar/order-details-scroll-bar';
import classNames from 'classnames';

export const OrderDetails = () => {
  const isOrderDetailsVisible = useVisibilityObserver(
    'pdp-continue-button',
    0.1
  );
  const [isLoading, setIsLoading] = useState(false);
  const [loadingImage, setLoadingImage] = useState(true);
  const [hasTriggeredEvent, setHasTriggeredEvent] = useState(false);
  const [hasInstantPaper, setHasInstantPaper] = useState(false);
  const shouldShowScrollBar = isOrderDetailsVisible.isVisible;
  const isBelowComponent = isOrderDetailsVisible.isBelow;
  const configuration = useReactiveVar(configurationState);
  const skuList = getSkuList(useSearchParams()?.get('skus'));
  const client = useApolloClient();
  const { isSMB } = useContext(FlowOrchestratorContext);
  const {
    data: { products },
  } = useSuspenseQuery<GetSkuProductsQuery>(GetSkuProductsDocument, {
    variables: { skuList },
  });

  const {
    pdpConfig: { prices, selected_options },
  } = client.readQuery<GetPdpStateQuery>({
    query: GetPdpStateDocument,
    variables: { skuList, configuration },
  });
  const multipleProduct = products?.items as BundleProductWithLowestPrice[];
  const activeSKU = configuration.activeSku as string;
  const configs = configuration[activeSKU] as ProductOptions;
  const currencyFromProducts =
    multipleProduct?.[0]?.price_range?.minimum_price?.regular_price?.currency ||
    'USD';

  useEffect(() => {
    const storedPaperOption = sessionStorage.getItem('selectedPaperOption');
    if (storedPaperOption) {
      setHasInstantPaper(true);
    }
  }, []);

  useEffect(() => {
    const attentive = AttentiveAnalyticsHandler.getInstance();
    attentive.setProductView({
      products,
      activeSKU,
      selectedOptions: selected_options,
    });
  }, [selected_options]);

  useEffect(() => {
    if (configs?.['selectedPaperOption']) {
      setHasInstantPaper(true);
      if (!sessionStorage.getItem('selectedPaperOption')) {
        sessionStorage.setItem('selectedPaperOption', 'true');
      }
      delete configs['selectedPaperOption'];
    }
  }, [configs]);
  const arrayActiveItems = configs
    ? Object.entries(configuration[activeSKU])
    : [];

  const planType: { [key: string]: string } = {
    instantInk: 'pdpConfig.orderDetails.printPlan',
  };

  const selectedProductItems = activeSKU
    ? (
        products?.items?.find(
          (product) => product.sku === activeSKU
        ) as BundleProductWithLowestPrice
      )?.items
    : [];

  const mainProduct =
    activeSKU && selectedProductItems?.[0]?.options?.[0].product;

  // Get required items and options
  const requiredItems = getRequiredItems(selectedProductItems);
  // Check if all required options have been selected
  const hasRequiredOptionsBeenSelected = requiredOptionsSelected(
    selected_options,
    requiredItems
  );

  // Get optional items (instant paper)
  const optionalItems = getOptionalItems(selectedProductItems);
  // Check if all optional item have been selected
  const hasOptionalOptionsBeenSelected = optionalOptionsSelected(
    optionalItems,
    hasInstantPaper
  );

  const { t } = useTranslation(NamespacesI18n.PDP_CONFIG);
  const viewSize = useCheckViewSize();
  const includes = t('pdpConfig.orderDetails.includes', {
    returnObjects: true,
  }) as [];
  const bundleProduct =
    multipleProduct?.find((product) => product.sku === activeSKU) ??
    multipleProduct?.[0];

  const bundleLevelImage = getBundleLevelImage({
    activeSKU,
    bundleProduct,
    viewSize,
    isSMB,
  });

  useEffect(() => {
    if (!loadingImage) return;

    setLoadingImage(true);
  }, [bundleLevelImage]);
  const paperOrInkLabel = (
    activeItem: BundleItemOptionWithLowestPrice,
    itemUid: string,
    optionUid: string
  ) => {
    if (getProductTypeLabelFromOption(activeItem) === 'instantPaper') {
      // If active item is target state type and has not selected paper, this will prevent adding the "Paper Add-On Service"
      if (activeItem?.price === 0 && activeItem?.label === 'New Instant Paper')
        return;
      const pages = getActiveItemInfo(itemUid, optionUid, selectedProductItems)
        ?.product?.hp_pages_per_month;
      return t('pdpConfig.orderDetails.instantPaper', {
        pages: pages,
      });
    }
    const pages = getMonthlyPages(activeItem);
    return t('pdpConfig.orderDetails.pages', { pages });
  };

  const isContinueButtonDisabled =
    !hasRequiredOptionsBeenSelected || !hasOptionalOptionsBeenSelected;

  return (
    <>
      <div className={styles['container']} id="order-details-container">
        <div
          className={styles[getImageClass(activeSKU, 'image-container', false)]}
        >
          <div className={`subtitle-large ${styles['title-mobile']}`}>
            <span>{t('pdpConfig.orderDetails.title')}</span>
          </div>
          <Image
            src={bundleLevelImage}
            unoptimized
            loading="lazy"
            quality={100}
            width={0}
            height={0}
            alt={'order-image'}
            id="order-details-image"
            className={styles[getImageClass(activeSKU, 'image', loadingImage)]}
            onLoad={() => setLoadingImage(false)}
          />
        </div>
        <div className={styles['options-container']}>
          <div
            className={`subtitle-large ${styles['options']}`}
            id="order-details"
          >
            <span className={`subtitle-large ${styles['title']}`}>
              {t('pdpConfig.orderDetails.title')}
            </span>

            {activeSKU && (
              <div className={styles['infos-container']}>
                <div className={styles['plan-infos']}>
                  <span
                    className={classNames(
                      styles['category-type'],
                      'body-extra-large'
                    )}
                  >
                    {classifyPrinter(bundleProduct)}
                  </span>
                  <span
                    className={classNames(
                      styles['category-name'],
                      'body-large'
                    )}
                  >
                    {mainProduct?.hp_short_config_name}
                  </span>
                </div>
                {arrayActiveItems?.map(([itemUid, optionUid]) => {
                  const activeItem = getActiveItemInfo(
                    itemUid,
                    optionUid,
                    selectedProductItems
                  );

                  return (
                    <div key={itemUid} className={styles['plan-infos']}>
                      <span
                        className={classNames(
                          styles['category-type'],
                          'body-extra-large'
                        )}
                      >
                        {paperOrInkLabel(activeItem, itemUid, optionUid)}
                      </span>
                      {shouldShowDescriptions(activeItem, planType) && (
                        <span
                          className={classNames(
                            styles['category-name'],
                            'body-large'
                          )}
                        >
                          {t(
                            planType[activeItem?.product.hp_product_type_label],
                            { plan: activeItem?.label }
                          )}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <hr className={styles['divider']} />
            {activeSKU && (
              <div className={styles['title-price']}>
                <span className="title-small">
                  {t(
                    `${
                      configs
                        ? 'pdpConfig.orderDetails.priceTotal'
                        : 'pdpConfig.orderDetails.priceRange'
                    }`
                  )}
                </span>
                <span className="title-small">
                  {t('pdpConfig.orderDetails.price', {
                    price: formatPrice({
                      selectedOptions: selected_options,
                      totalPricePerMonth: prices?.totalPricePerMonth,
                      products:
                        products?.items as BundleProductWithLowestPrice[],
                      currencyCode: currencyFromProducts,
                    }),
                    currencySign: getCurrencySymbol(currencyFromProducts),
                  })}
                </span>
              </div>
            )}
            <div className={styles['continue-container']}>
              <ThemeProviderWrapperV2>
                <ContinueButton
                  isDisabled={isContinueButtonDisabled}
                  loading={isLoading}
                  isLoading={setIsLoading}
                  hasTriggeredEvent={hasTriggeredEvent}
                  setHasTriggeredEvent={setHasTriggeredEvent}
                />
              </ThemeProviderWrapperV2>
            </div>
            <ul className={styles['plan-includes']}>
              {includes.map((include: string) => (
                <li key={include} className={styles['include']}>
                  <IconCheckmark color="gray7" size={20} />
                  <span
                    className={`caption-regular ${styles['includes-item']}`}
                  >
                    {include}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      {!shouldShowScrollBar && (
        <OrderDetailsScrollBar
          isContinueButtonDisabled={isContinueButtonDisabled}
          loading={isLoading}
          isLoading={setIsLoading}
          hasTriggeredEvent={hasTriggeredEvent}
          setHasTriggeredEvent={setHasTriggeredEvent}
          currencyCode={currencyFromProducts}
          isBelow={isBelowComponent}
        />
      )}
    </>
  );
};

export default OrderDetails;
